import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import { useReactToPrint } from "react-to-print";
import api from "../services/api";
import StickerPrintArea, { STICKER_PAGE_STYLE } from "./StickerPrintArea";
import { todayISO } from "../utils/date";

const PACK_TYPES = ["Boxes", "Pallets", "Bales"];

/* ================= MAIN ================= */
export default function PackingList() {
  const [editId, setEditId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <Box sx={{ width: "100%" }}>
      <PackingListEditor editId={editId} setEditId={setEditId} onSaved={() => setReloadKey((k) => k + 1)} />
      <PackingListTable key={reloadKey} onEdit={setEditId} />
    </Box>
  );
}

/* ================= EDITOR ================= */
function PackingListEditor({ editId, setEditId, onSaved }) {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);

  const today = todayISO();

  const emptyMaster = useMemo(() => ({
    Master_ID: null,
    PackingList_No: "",
    Date: today,
    Customer_ID: null,
    Customer_Name: "",
    Notes: "",
  }), [today]);

  const emptyDetail = useMemo(() => ({
    Item_ID: null,
    Item_Description: "",
    PackType: "Boxes",
    PackQty: 0,
    UnitWeight: 0,
    TotalWeight: 0,
    Rate: 0,
    Notes: "",
    Boxes: [], // [{ Box_ID, StickerCode, NetWt, GrossWt, TareWt, PackType, PackedAt, Item_Description }]
  }), []);

  const [master, setMaster] = useState(emptyMaster);
  const [details, setDetails] = useState([{ ...emptyDetail }]);

  // ----- print plumbing -----
  const printRef = useRef(null);
  const [printQueue, setPrintQueue] = useState([]);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: STICKER_PAGE_STYLE,
    documentTitle: "Packing List Stickers",
    onAfterPrint: () => setPrintQueue([]),
  });
  useEffect(() => {
    if (printQueue.length && handlePrint) handlePrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printQueue]);

  // Separate printable copy of the packing list itself (not the stickers).
  const packingListPrintRef = useRef(null);
  const handlePrintPackingList = useReactToPrint({
    contentRef: packingListPrintRef,
    documentTitle: `Packing-List-${master.PackingList_No || master.Date || ""}`,
    pageStyle: "@page { size: Letter; margin: 0.5in; }",
  });

  // ----- "Assign Boxes" dialog -----
  const [boxDialog, setBoxDialog] = useState({ open: false, rowIndex: -1 });

  const resetForm = useCallback(() => {
    setMaster(emptyMaster);
    setDetails([{ ...emptyDetail }]);
  }, [emptyMaster, emptyDetail]);

  /* === Load dropdowns === */
  useEffect(() => {
    api.get("/packingList/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load products:", e));

    api.get("/packingList/customers")
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load customers:", e));
  }, []);

  /* === Load record for edit === */
  useEffect(() => {
    if (!editId) return;
    api.get(`/packingList/${editId}`)
      .then((res) => {
        const m = res.data?.master || {};
        const d = res.data?.details || [];
        setMaster({
          Master_ID: m.Master_ID || null,
          PackingList_No: m.PackingList_No || "",
          Date: m.Date ? String(m.Date).split("T")[0] : today,
          Customer_ID: m.Customer_ID || null,
          Customer_Name: m.Customer_Name || "",
          Notes: m.Notes || "",
        });
        setDetails(d.length ? d.map((row) => ({
          Item_ID: row.Item_ID || null,
          Item_Description: row.Item_Description || "",
          PackType: row.PackType || "Boxes",
          PackQty: Number(row.PackQty) || 0,
          UnitWeight: Number(row.UnitWeight) || 0,
          TotalWeight: Number(row.TotalWeight) || 0,
          Rate: Number(row.Rate) || 0,
          Notes: row.Notes || "",
          Boxes: Array.isArray(row.Boxes) ? row.Boxes : [],
        })) : [{ ...emptyDetail }]);
      })
      .catch((err) => {
        console.error("Failed to load packing list:", err);
        resetForm();
      });
  }, [editId, today, emptyDetail, resetForm]);

  /* === Detail row helpers === */
  const recalcFromBoxes = (row) => {
    if (row.Boxes && row.Boxes.length) {
      const qty = row.Boxes.length;
      const totalNet = row.Boxes.reduce((s, b) => s + (Number(b.NetWt) || 0), 0);
      row.PackQty = qty;
      row.TotalWeight = +totalNet.toFixed(2);
      row.UnitWeight = qty ? +(totalNet / qty).toFixed(2) : 0;
    } else {
      const q = Number(row.PackQty) || 0;
      const u = Number(row.UnitWeight) || 0;
      row.TotalWeight = +(q * u).toFixed(2);
    }
    return row;
  };

  const updateRow = (i, field, value) => {
    setDetails((prev) => {
      const rows = [...prev];
      const row = { ...rows[i] };

      if (field === "Item") {
        row.Item_ID = value?.id || null;
        row.Item_Description = value?.Product_Description || "";
        // If product changes, drop any previously attached boxes (they were for the old product)
        row.Boxes = [];
      } else if (field === "PackType") {
        row.PackType = value || "Boxes";
      } else if (field === "Notes") {
        row.Notes = value || "";
      } else {
        row[field] = value === "" ? 0 : Number(value);
      }
      recalcFromBoxes(row);
      rows[i] = row;
      return rows;
    });
  };

  const addRow = () => setDetails((prev) => [...prev, { ...emptyDetail }]);
  const removeRow = (i) => setDetails((prev) => {
    const next = prev.filter((_, idx) => idx !== i);
    return next.length ? next : [{ ...emptyDetail }];
  });

  const removeBoxFromRow = (rowIndex, boxId) => {
    setDetails((prev) => {
      const rows = [...prev];
      const row = { ...rows[rowIndex] };
      row.Boxes = (row.Boxes || []).filter((b) => b.Box_ID !== boxId);
      recalcFromBoxes(row);
      rows[rowIndex] = row;
      return rows;
    });
  };

  const applyAssignedBoxes = (rowIndex, picked) => {
    setDetails((prev) => {
      const rows = [...prev];
      const row = { ...rows[rowIndex] };
      const existingIds = new Set((row.Boxes || []).map((b) => b.Box_ID));
      const merged = [...(row.Boxes || [])];
      for (const b of picked) {
        if (!existingIds.has(b.Box_ID)) merged.push(b);
      }
      row.Boxes = merged;
      // Snap PackType to the most common type in the boxes
      if (merged.length) row.PackType = merged[0].PackType || row.PackType;
      recalcFromBoxes(row);
      rows[rowIndex] = row;
      return rows;
    });
  };

  // Master "Add from Finished Goods" — one row PER box, even when several
  // boxes share the same product. Lets each sticker/weight show on its own
  // line on the packing list.
  const applyMultiAddFromFG = (picked) => {
    if (!picked || !picked.length) return;

    setDetails((prev) => {
      let rows = [...prev];
      // Drop the leading empty placeholder row if it has no product
      if (rows.length === 1 && !rows[0].Item_ID && !(rows[0].Boxes || []).length) {
        rows = [];
      }

      // Skip boxes already attached anywhere on this list to prevent dupes.
      const existingIds = new Set(
        rows.flatMap((r) => (r.Boxes || []).map((b) => b.Box_ID))
      );

      for (const b of picked) {
        if (existingIds.has(b.Box_ID)) continue;
        existingIds.add(b.Box_ID);

        const row = {
          ...emptyDetail,
          Item_ID: b.Item_ID,
          Item_Description: b.Item_Description || "",
          PackType: b.PackType || "Boxes",
          Boxes: [b],
        };
        recalcFromBoxes(row);
        rows.push(row);
      }
      return rows.length ? rows : [{ ...emptyDetail }];
    });
  };

  /* === Totals === */
  const totalQty    = details.reduce((s, r) => s + (Number(r.PackQty)     || 0), 0);
  const totalWeight = details.reduce((s, r) => s + (Number(r.TotalWeight) || 0), 0);

  // All attached boxes across rows — for the "Print all stickers" button
  const allBoxes = useMemo(() => {
    const out = [];
    for (const r of details) {
      for (const b of (r.Boxes || [])) {
        // ensure each box object has enough fields for the sticker
        out.push({
          Box_ID: b.Box_ID,
          StickerCode: b.StickerCode,
          Item_Description: b.Item_Description || r.Item_Description,
          PackType: b.PackType || r.PackType,
          PackedAt: b.PackedAt,
          GrossWt: b.GrossWt,
          TareWt: b.TareWt,
          NetWt: b.NetWt,
        });
      }
    }
    return out;
  }, [details]);

  /* === Save === */
  const saveList = async () => {
    if (!master.Customer_ID) {
      alert("Pick a customer first");
      return;
    }
    const filled = details
      .filter((d) => d.Item_ID && (Number(d.PackQty) > 0 || (d.Boxes || []).length > 0))
      .map((d) => ({
        ...d,
        BoxIds: (d.Boxes || []).map((b) => b.Box_ID),
      }));
    if (!filled.length) {
      alert("Add at least one item with quantity or attached boxes");
      return;
    }

    try {
      const payload = { master, details: filled };
      const res = editId
        ? await api.put(`/packingList/${editId}`, payload)
        : await api.post("/packingList", payload);

      alert(res.data?.message || "Saved");
      resetForm();
      setEditId(null);
      onSaved?.();
    } catch (err) {
      console.error("Save packing list failed:", err);
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    }
  };

  const cancelEdit = () => {
    resetForm();
    setEditId(null);
  };

  const printAllStickers = () => {
    if (!allBoxes.length) {
      alert("No boxes attached. Use 'Assign Boxes' on an item row first.");
      return;
    }
    setPrintQueue(allBoxes);
  };

  return (
    <Box p={1}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6">{editId ? "Edit Packing List" : "New Packing List"}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Record how a shipment of materials is packed. Use <b>Assign Boxes</b> on a row to attach
          active finished-goods boxes (with stickers) to this list.
        </Typography>

        {/* Master row */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              type="date"
              label="Date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={master.Date || ""}
              onChange={(e) => setMaster({ ...master, Date: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Packing List #"
              fullWidth
              placeholder="Optional reference"
              value={master.PackingList_No || ""}
              onChange={(e) => setMaster({ ...master, PackingList_No: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 7 }}>
            <Autocomplete
              options={customers}
              getOptionLabel={(o) => o?.SubAccount_Name || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={customers.find((c) => c.id === master.Customer_ID) || null}
              onChange={(e, v) =>
                setMaster({
                  ...master,
                  Customer_ID: v?.id || null,
                  Customer_Name: v?.SubAccount_Name || "",
                })
              }
              renderInput={(params) => <TextField {...params} label="Customer" fullWidth />}
              fullWidth
              sx={{ width: "100%" }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes (optional)"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={master.Notes || ""}
              onChange={(e) => setMaster({ ...master, Notes: e.target.value })}
            />
          </Grid>
        </Grid>

        {/* Items table */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
          Items
        </Typography>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Material</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Pack Type</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Attached Boxes / Stickers</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Qty</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Unit Wt</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Total Wt</TableCell>
                <TableCell sx={{ minWidth: 90 }}>Rate</TableCell>
                <TableCell sx={{ minWidth: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((r, i) => {
                const hasBoxes = (r.Boxes || []).length > 0;
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <Autocomplete
                        options={products}
                        getOptionLabel={(o) => o?.Product_Description || ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={products.find((p) => p.id === r.Item_ID) || null}
                        onChange={(e, v) => updateRow(i, "Item", v)}
                        renderInput={(params) => <TextField {...params} size="small" placeholder="Pick material" />}
                        sx={{ width: "100%", minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={r.PackType || "Boxes"}
                          onChange={(e) => updateRow(i, "PackType", e.target.value)}
                          disabled={hasBoxes}
                        >
                          {PACK_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                        {(r.Boxes || []).map((b) => (
                          <Chip
                            key={b.Box_ID}
                            size="small"
                            label={`${b.StickerCode} · ${(Number(b.NetWt) || 0).toFixed(2)}lb`}
                            onDelete={() => removeBoxFromRow(i, b.Box_ID)}
                          />
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!r.Item_ID}
                          onClick={() => setBoxDialog({ open: true, rowIndex: i })}
                        >
                          Assign Boxes
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.PackQty}
                        onChange={(e) => updateRow(i, "PackQty", e.target.value)}
                        inputProps={{ step: "0.01", min: "0" }}
                        sx={{ width: "100%" }}
                        disabled={hasBoxes}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.UnitWeight}
                        onChange={(e) => updateRow(i, "UnitWeight", e.target.value)}
                        inputProps={{ step: "0.01", min: "0" }}
                        sx={{ width: "100%" }}
                        disabled={hasBoxes}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {(Number(r.TotalWeight) || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.Rate}
                        onChange={(e) => updateRow(i, "Rate", e.target.value)}
                        inputProps={{ step: "0.01", min: "0" }}
                        sx={{ width: "100%" }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="error" size="small" onClick={() => removeRow(i)} aria-label="remove row">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1 }}>
          <Button onClick={addRow}>+ Add Item</Button>
        </Box>

        {/* Totals strip */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Total Pack Qty</Typography>
            <Typography sx={{ fontWeight: 600 }}>{totalQty.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Total Weight</Typography>
            <Typography sx={{ fontWeight: 600 }}>{totalWeight.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Lines</Typography>
            <Typography sx={{ fontWeight: 600 }}>{details.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Stickers</Typography>
            <Typography sx={{ fontWeight: 600 }}>{allBoxes.length}</Typography>
          </Box>
        </Box>

        <Box mt={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={saveList}>{editId ? "Update" : "Save"}</Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setBoxDialog({ open: true, rowIndex: -1 })}
          >
            Add from Finished Goods
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printAllStickers}>
            Print All Stickers
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintPackingList}
            disabled={!details.some((d) => d.Item_ID)}
          >
            Print Packing List
          </Button>
          {editId && <Button onClick={cancelEdit}>Cancel</Button>}
        </Box>
      </Paper>

      {/* Assign-boxes dialog. rowIndex >= 0 = per-row (filtered by product);
          rowIndex = -1 = master add-from-FG (browse all products). */}
      <AssignBoxesDialog
        open={boxDialog.open}
        onClose={() => setBoxDialog({ open: false, rowIndex: -1 })}
        itemId={boxDialog.rowIndex >= 0 ? details[boxDialog.rowIndex]?.Item_ID : null}
        existingIds={
          boxDialog.rowIndex >= 0
            ? (details[boxDialog.rowIndex]?.Boxes || []).map((b) => b.Box_ID)
            : details.flatMap((r) => (r.Boxes || []).map((b) => b.Box_ID))
        }
        allowAnyProduct={boxDialog.rowIndex === -1}
        onConfirm={(picked) => {
          if (boxDialog.rowIndex >= 0) applyAssignedBoxes(boxDialog.rowIndex, picked);
          else applyMultiAddFromFG(picked);
          setBoxDialog({ open: false, rowIndex: -1 });
        }}
      />

      {/* Hidden print surface */}
      <StickerPrintArea ref={printRef} boxes={printQueue} />

      {/* Hidden print surface for the packing list document */}
      <Box sx={{ display: "none", "@media print": { display: "block" } }}>
        <PackingListPrintView
          ref={packingListPrintRef}
          master={master}
          details={details}
          totalQty={totalQty}
          totalWeight={totalWeight}
        />
      </Box>
    </Box>
  );
}

/* ================= PRINTABLE PACKING LIST ================= */
const PackingListPrintView = React.forwardRef(function PackingListPrintView(
  { master, details, totalQty, totalWeight },
  ref,
) {
  const fmt = (n) => (Number(n) || 0).toFixed(2);
  const boxGross = (d) => (d.Boxes || []).reduce((s, b) => s + (Number(b.GrossWt) || 0), 0);
  const boxTare  = (d) => (d.Boxes || []).reduce((s, b) => s + (Number(b.TareWt)  || 0), 0);
  const rows = details.filter((d) => d.Item_ID);
  const totalGross = rows.reduce((s, d) => s + boxGross(d), 0);
  const totalTare  = rows.reduce((s, d) => s + boxTare(d), 0);
  return (
    <div
      ref={ref}
      style={{
        fontFamily: "Arial, sans-serif",
        color: "#000",
        padding: "0.25in",
        fontSize: 12,
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2 }}>PACKING LIST</div>
        <div style={{ fontSize: 14, marginTop: 2 }}>Glomets, Inc.</div>
      </div>

      <table style={{ width: "100%", marginBottom: 12, fontSize: 12 }}>
        <tbody>
          <tr>
            <td style={{ padding: "2px 0", width: "20%" }}><b>PL #:</b></td>
            <td style={{ padding: "2px 0", width: "30%" }}>{master.PackingList_No || "-"}</td>
            <td style={{ padding: "2px 0", width: "20%" }}><b>Date:</b></td>
            <td style={{ padding: "2px 0", width: "30%" }}>{master.Date || "-"}</td>
          </tr>
          <tr>
            <td style={{ padding: "2px 0" }}><b>Customer:</b></td>
            <td style={{ padding: "2px 0" }} colSpan={3}>{master.Customer_Name || "-"}</td>
          </tr>
        </tbody>
      </table>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 11,
          marginBottom: 12,
        }}
      >
        <thead>
          <tr style={{ background: "#eee" }}>
            <th style={cell()}>#</th>
            <th style={cell({ textAlign: "left" })}>Material</th>
            <th style={cell()}>Sticker</th>
            <th style={cell()}>Type</th>
            <th style={cell({ textAlign: "right" })}>Gross Wt</th>
            <th style={cell({ textAlign: "right" })}>Tare Wt</th>
            <th style={cell({ textAlign: "right" })}>Net Wt</th>
            <th style={cell({ textAlign: "right" })}>Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => {
            const sticker = (d.Boxes && d.Boxes[0] && d.Boxes[0].StickerCode) || "";
            return (
              <tr key={i}>
                <td style={cell()}>{i + 1}</td>
                <td style={cell({ textAlign: "left" })}>{d.Item_Description}</td>
                <td style={cell()}>{sticker}</td>
                <td style={cell()}>{d.PackType}</td>
                <td style={cell({ textAlign: "right" })}>{fmt(boxGross(d))}</td>
                <td style={cell({ textAlign: "right" })}>{fmt(boxTare(d))}</td>
                <td style={cell({ textAlign: "right" })}>{fmt(d.TotalWeight)}</td>
                <td style={cell({ textAlign: "right" })}>{fmt(d.Rate)}</td>
              </tr>
            );
          })}
          <tr>
            <td style={cell({ fontWeight: 700 })} colSpan={4}>TOTAL</td>
            <td style={cell({ textAlign: "right", fontWeight: 700 })}>{fmt(totalGross)}</td>
            <td style={cell({ textAlign: "right", fontWeight: 700 })}>{fmt(totalTare)}</td>
            <td style={cell({ textAlign: "right", fontWeight: 700 })}>{fmt(totalWeight)}</td>
            <td style={cell()} />
          </tr>
        </tbody>
      </table>

      {master.Notes ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Notes</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{master.Notes}</div>
        </div>
      ) : null}

      <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: 3 }}>Packed By</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: 3 }}>Received By</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: 3 }}>Date</div>
        </div>
      </div>
    </div>
  );
});

function cell(extra) {
  return {
    border: "1px solid #999",
    padding: "4px 6px",
    textAlign: "center",
    ...(extra || {}),
  };
}

/* ================= ASSIGN BOXES DIALOG ================= */
function AssignBoxesDialog({ open, onClose, itemId, existingIds, onConfirm, allowAnyProduct }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});  // { Box_ID: box }

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setSearch("");
  }, [open]);

  const fetchActive = useCallback(() => {
    if (!open) return;
    const params = {};
    if (itemId) params.itemId = itemId;
    if (search) params.search = search;
    api.get("/finishedGoods/active", { params })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load active boxes:", e));
  }, [open, itemId, search]);
  useEffect(() => { fetchActive(); }, [fetchActive]);

  const toggle = (b) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[b.Box_ID]) delete next[b.Box_ID];
      else next[b.Box_ID] = b;
      return next;
    });
  };

  const pickedList = Object.values(picked);
  const pickedTotal = pickedList.reduce((s, b) => s + (Number(b.NetWt) || 0), 0);
  const existing = new Set(existingIds);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {allowAnyProduct
          ? "Add from Finished Goods (all products)"
          : "Assign Active Finished-Goods Boxes"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Search sticker / product"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={fetchActive}>Search</Button>
        </Box>

        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Sticker</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Tare</TableCell>
                <TableCell align="right">Net</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>
                    No active boxes match
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const already = existing.has(r.Box_ID);
                return (
                  <TableRow
                    key={r.Box_ID}
                    hover
                    sx={{ opacity: already ? 0.45 : 1, cursor: already ? "not-allowed" : "pointer" }}
                    onClick={() => { if (!already) toggle(r); }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!picked[r.Box_ID] || already}
                        disabled={already}
                        onChange={() => toggle(r)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.StickerCode}</TableCell>
                    <TableCell>{r.Item_Description}</TableCell>
                    <TableCell>{r.PackType}</TableCell>
                    <TableCell>{r.PackedAt ? String(r.PackedAt).split("T")[0] : "-"}</TableCell>
                    <TableCell align="right">{(Number(r.GrossWt) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{(Number(r.TareWt)  || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {(Number(r.NetWt) || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: "flex", gap: 3, alignItems: "center" }}>
          <Typography variant="body2">
            Selected: <b>{pickedList.length}</b>
          </Typography>
          <Typography variant="body2">
            Net weight total: <b>{pickedTotal.toFixed(2)} lb</b>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!pickedList.length} onClick={() => onConfirm(pickedList)}>
          Add {pickedList.length || ""} Box{pickedList.length === 1 ? "" : "es"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================= LIST ================= */
function PackingListTable({ onEdit }) {
  const today = todayISO();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [activeOnly, setActiveOnly] = useState(false);

  // Print a packing list straight from the list (no need to open Edit).
  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);
  const handlePrintList = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Packing-List-${printData?.master?.PackingList_No || printData?.master?.Date || ""}`,
    pageStyle: "@page { size: Letter; margin: 0.5in; }",
    onAfterPrint: () => setPrintData(null),
  });
  useEffect(() => {
    if (printData) handlePrintList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printData]);

  const viewPrint = async (r) => {
    try {
      const res = await api.get(`/packingList/${r.Master_ID}`);
      const details = res.data?.details || [];
      const master = {
        ...(res.data?.master || {}),
        Customer_Name: r.Customer_Name,
        PackingList_No: r.PackingList_No,
        Date: r.Date ? String(r.Date).split("T")[0] : "",
      };
      const totalWeight = details.reduce((s, d) => s + (Number(d.TotalWeight) || 0), 0);
      setPrintData({ master, details, totalQty: 0, totalWeight });
    } catch (e) {
      alert("Failed to open packing list: " + (e.response?.data?.error || e.message));
    }
  };

  const fetchRows = useCallback(() => {
    api.get("/packingList", {
      params: { name: search, fromDate, toDate, activeOnly: activeOnly ? "1" : "" },
    })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load packing lists:", e));
  }, [search, fromDate, toDate, activeOnly]);

  useEffect(() => fetchRows(), [fetchRows]);

  const del = async (id) => {
    if (!window.confirm("Delete this packing list?")) return;
    try {
      await api.delete(`/packingList/${id}`);
      setRows((prev) => prev.filter((x) => x.Master_ID !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={1}>
      <Typography variant="h6" sx={{ mb: 1 }}>Past Packing Lists</Typography>

      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Customer / Ref #" fullWidth value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="active-filter-label">Show</InputLabel>
              <Select
                labelId="active-filter-label"
                label="Show"
                value={activeOnly ? "1" : "0"}
                onChange={(e) => setActiveOnly(e.target.value === "1")}
              >
                <MenuItem value="0">All</MenuItem>
                <MenuItem value="1">Active only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Ref #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Lines</TableCell>
              <TableCell>Total Qty</TableCell>
              <TableCell>Total Wt</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: "text.secondary" }}>
                  No packing lists in this date range
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.Master_ID}>
                  <TableCell>{r.Master_ID}</TableCell>
                  <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                  <TableCell>{r.PackingList_No || "-"}</TableCell>
                  <TableCell>{r.Customer_Name || "-"}</TableCell>
                  <TableCell>{r.LineCount}</TableCell>
                  <TableCell>{(Number(r.TotalPackQty) || 0).toFixed(2)}</TableCell>
                  <TableCell>{(Number(r.TotalWeight) || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {r.Active ? (
                      <Chip size="small" label="Active" color="success" />
                    ) : (
                      <Chip size="small" label="Used / Inactive" />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Button size="small" color="success" onClick={() => viewPrint(r)}>View</Button>
                    <Button size="small" onClick={() => onEdit(r.Master_ID)}>Edit</Button>
                    {r.Active === true || r.Active === 1 ? (
                      <Button size="small" color="error" onClick={() => del(r.Master_ID)}>Delete</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Hidden print surface — lets you print a packing list without editing it */}
      <Box sx={{ display: "none", "@media print": { display: "block" } }}>
        {printData && (
          <PackingListPrintView
            ref={printRef}
            master={printData.master}
            details={printData.details}
            totalQty={printData.totalQty}
            totalWeight={printData.totalWeight}
          />
        )}
      </Box>
    </Box>
  );
}
